import { getStatusConfigRedirectHrefWithVendorContext } from "@/utils/statusConfigUrlAlignment";

/** List module paths — mirror `goToProductPage` in `navigation.ts`. */
export const PANEL_LIST_MODULE_PATHS = {
  SHOPPING_LIST: "/create-product/shopping-list-management",
  TASTING_PANEL: "/panel/tasting-panel",
  BUSINESS_REVIEW_PANEL: "/panel/business-review-panel",
  PREPARE_FOR_PANEL: "/create-product/prepare-for-panel",
  VENDOR_ASSESSMENT: "/create-product/vendor-assessment",
} as const;

export type PanelListNavVendor = {
  id?: string | number;
  vendorAssessmentStatus?: string;
  businessStatus?: string;
  vendorAssessmentId?: string | number;
};

export type PanelListNavRow = {
  listItemForNavigation: {
    id?: string | number;
    vendorStatus?: PanelListNavVendor[] | null;
  };
  vendorDetailForNavigation: PanelListNavVendor | null;
};

/**
 * Resolve the full vendor object for navigation. When `vendorId` is passed (e.g. multi-vendor
 * tooltip on a parent row), look up the vendor in `listItemForNavigation.vendorStatus` so status
 * fields are available for status-config routing.
 */
export function resolvePanelListVendorForNavigation(
  row: PanelListNavRow,
  vendorId?: string | number
): PanelListNavVendor | null {
  if (vendorId == null) {
    return row.vendorDetailForNavigation;
  }

  const targetId = String(vendorId).trim();
  if (!targetId) {
    return row.vendorDetailForNavigation;
  }

  const navVendor = row.vendorDetailForNavigation;
  if (navVendor?.id != null && String(navVendor.id).trim() === targetId) {
    return navVendor;
  }

  const vendors = Array.isArray(row.listItemForNavigation.vendorStatus)
    ? row.listItemForNavigation.vendorStatus
    : [];
  const match = vendors.find((v) => v?.id != null && String(v.id).trim() === targetId);
  if (match) {
    return match;
  }

  return { id: vendorId };
}

function buildPanelListModuleFallbackHref(
  listModulePath: string,
  productId: string | number,
  vendorId: string
): string {
  const base = listModulePath.replace(/\/+$/, "");
  const path = `${base}/${encodeURIComponent(String(productId))}`;
  const q = new URLSearchParams({ vendorId });
  return `${path}?${q.toString()}`;
}

export type NavigatePanelListVendorFromRowOptions = {
  row: PanelListNavRow;
  vendorId?: string | number;
  /** When already resolved (e.g. by `usePanelListRowNavigation`), skip a second lookup. */
  vendor?: PanelListNavVendor | null;
  listModulePath: string;
  pathname: string | null;
  push: (href: string) => void;
};

/**
 * Status-aware navigation from panel infinite-scroll lists: redirect to the workflow module
 * defined in `status-config.json` when the vendor's status does not belong on the list module,
 * otherwise fall back to the list module detail URL.
 */
export function navigatePanelListVendorFromRow(options: NavigatePanelListVendorFromRowOptions): boolean {
  const { row, vendorId, vendor: vendorOverride, listModulePath, pathname, push } = options;

  const vendor = vendorOverride ?? resolvePanelListVendorForNavigation(row, vendorId);
  if (vendor?.id == null || String(vendor.id).trim() === "") {
    return false;
  }

  const productId = row.listItemForNavigation.id;
  if (productId == null || String(productId).trim() === "") {
    return false;
  }

  const vid = String(vendor.id).trim();
  const status = String(vendor.vendorAssessmentStatus ?? vendor.businessStatus ?? "").trim();
  const vendorAssessmentId =
    vendor.vendorAssessmentId != null && String(vendor.vendorAssessmentId).trim() !== ""
      ? String(vendor.vendorAssessmentId).trim()
      : null;

  const currentPathForStatus =
    pathname != null && pathname.includes(listModulePath) ? pathname : listModulePath;

  if (status) {
    const moduleHref = getStatusConfigRedirectHrefWithVendorContext({
      status,
      currentPathOrHref: currentPathForStatus,
      productId,
      vendorId: vid,
      vendorAssessmentId,
      activeProjectsStaticTabKey: null,
    });
    if (moduleHref) {
      push(moduleHref);
      return true;
    }
  }

  push(buildPanelListModuleFallbackHref(listModulePath, productId, vid));
  return true;
}
